import { Session } from '@synonymdev/pubky';
import type { Pubky } from '@/models/models.types';

export interface AuthInitParams {
  currentUserPubky: Pubky | null;
  session: Session | null;
  /** null = unknown/undetermined, false = no profile, true = has profile */
  hasProfile: boolean | null;
}

export interface AuthState extends AuthInitParams {
  sessionExport: string | null;
  /** SDK IndexedDB record for a delegated Passport grant; contains no credentials. */
  storedSessionId: string | null;
  passportInstance: string | null;
  hasHydrated: boolean;
  isRestoringSession: boolean;
  /** Whether a restored session's undetermined profile state is being resolved with the homeserver */
  isResolvingProfile: boolean;
  /** Whether the sign-in dialog is open (for unauthenticated users) */
  showSignInDialog: boolean;
  /** Whether a logout is in progress (prevents flash of weird states during logout) */
  isLoggingOut: boolean;
}

export interface AuthActions {
  reset: () => void;
  init: (params: AuthInitParams) => void;
  setCurrentUserPubky: (pubky: Pubky | null) => void;
  setSession: (session: Session | null) => void;
  setStoredSessionId: (id: string | null) => void;
  setPassportInstance: (instance: string) => void;
  setIsRestoringSession: (isRestoringSession: boolean) => void;
  setIsResolvingProfile: (isResolvingProfile: boolean) => void;
  setHasProfile: (hasProfile: boolean) => void;
  setHasHydrated: (hasHydrated: boolean) => void;
  /** Open or close the sign-in dialog */
  setShowSignInDialog: (show: boolean) => void;
  /** Set whether a logout is in progress */
  setIsLoggingOut: (isLoggingOut: boolean) => void;
}

export interface AuthSelectors {
  selectCurrentUserPubky: () => Pubky;
  selectIsAuthenticated: () => boolean;
  selectSession: () => Session | null;
}

export type AuthStore = AuthState & AuthActions & AuthSelectors;

export const authInitialState: AuthState = {
  currentUserPubky: null,
  session: null,
  sessionExport: null,
  storedSessionId: null,
  passportInstance: null,
  hasProfile: null,
  hasHydrated: false,
  isRestoringSession: false,
  isResolvingProfile: false,
  showSignInDialog: false,
  isLoggingOut: false,
};

export enum AuthActionTypes {
  INIT = 'INIT',
  RESET = 'RESET',
  SET_PUBKY = 'SET_PUBKY',
  SET_PASSPORT_INSTANCE = 'SET_PASSPORT_INSTANCE',
  SET_SESSION = 'SET_SESSION',
  SET_STORED_SESSION_ID = 'SET_STORED_SESSION_ID',
  CLEAR_SESSION = 'CLEAR_SESSION',
  SET_IS_RESTORING_SESSION = 'SET_IS_RESTORING_SESSION',
  SET_IS_RESOLVING_PROFILE = 'SET_IS_RESOLVING_PROFILE',
  SET_HAS_PROFILE = 'SET_HAS_PROFILE',
  SET_HAS_HYDRATED = 'SET_HAS_HYDRATED',
  SET_SHOW_SIGN_IN_DIALOG = 'SET_SHOW_SIGN_IN_DIALOG',
  SET_IS_LOGGING_OUT = 'SET_IS_LOGGING_OUT',
}
